import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Search, Clock, Database } from 'lucide-react';
import { Button } from './ui/button';
import { jemAPI } from '../utils/api';
import { toast } from 'sonner';

const fmtVal = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (Array.isArray(v)) return v.length ? JSON.stringify(v) : '[]';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const isEmpty = (v) => v === null || v === undefined || v === ''
  || (Array.isArray(v) && v.length === 0)
  || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v || {}).length === 0);

export default function JemPayloadModal({ siteId, device, onClose }) {
  const [state, setState] = useState(null);
  const [samples, setSamples] = useState([]);
  const [selected, setSelected] = useState('live');
  const [query, setQuery] = useState('');
  const [hideEmpty, setHideEmpty] = useState(true);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        jemAPI.getLatestPayload(siteId, device.device_id),
        jemAPI.getRawSamples(siteId, { device_id: device.device_id, limit: 15, include_payload: true }),
      ]);
      setState(s.data);
      setSamples(r.data.samples || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the device payload');
    } finally {
      setLoading(false);
    }
  }, [siteId, device.device_id]);

  useEffect(() => { load(); }, [load]);

  const payload = useMemo(() => {
    if (selected === 'live') return state?.latest_payload || {};
    const s = samples.find((x) => x.sample_id === selected);
    return s?.payload || {};
  }, [selected, state, samples]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return Object.entries(payload)
      .filter(([k, v]) => (!hideEmpty || !isEmpty(v)))
      .filter(([k]) => !q || k.toLowerCase().includes(q))
      .sort(([a], [b]) => a.localeCompare(b));
  }, [payload, query, hideEmpty]);

  const totalFields = Object.keys(payload).length;
  const emptyCount = Object.values(payload).filter(isEmpty).length;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-6 overflow-y-auto"
         data-testid="jem-payload-modal">
      <div className="bg-white rounded-sm border border-slate-300 w-full max-w-4xl shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-slate-700" />
              <h3 className="text-sm font-semibold text-slate-900">
                {device.device_label} · {device.device_id}
              </h3>
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600">
                {device.live_endpoint}
              </span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Every field the manufacturer sends for this device · vendor name {device.vendor_device_name || '—'}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={onClose} data-testid="jem-payload-close">
            <X className="w-3 h-3" />
          </Button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="w-3 h-3 text-slate-400 absolute left-2 top-2.5" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter fields…"
              className="w-56 text-sm border border-slate-300 rounded-sm pl-7 pr-2 py-1"
              data-testid="jem-payload-search"
            />
          </div>
          <label className="flex items-center gap-1.5 text-[12px] text-slate-600">
            <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)}
                   data-testid="jem-payload-hide-empty" />
            Hide empty fields{emptyCount ? ` (${emptyCount})` : ''}
          </label>
          <div className="flex items-center gap-1.5 ml-auto">
            <Clock className="w-3 h-3 text-slate-400" />
            <select value={selected} onChange={(e) => setSelected(e.target.value)}
                    className="text-[12px] border border-slate-300 rounded-sm px-2 py-1"
                    data-testid="jem-payload-snapshot-select">
              <option value="live">Latest reading{state?.vendor_ts ? ` · ${new Date(state.vendor_ts).toLocaleString()}` : ''}</option>
              {samples.map((s) => (
                <option key={s.sample_id} value={s.sample_id}>
                  Stored change · {new Date(s.vendor_ts || s.fetched_at).toLocaleString()}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="px-5 py-10 text-center text-[12px] text-slate-500" data-testid="jem-payload-loading">
            Loading payload…
          </div>
        ) : (
          <div className="max-h-[55vh] overflow-y-auto">
            <table className="w-full text-[12px]" data-testid="jem-payload-table">
              <tbody>
                {rows.length === 0 && (
                  <tr><td className="px-5 py-8 text-center text-slate-500" data-testid="jem-payload-empty">
                    No fields match this filter.
                  </td></tr>
                )}
                {rows.map(([k, v]) => (
                  <tr key={k} className="border-b border-slate-100" data-testid={`jem-payload-row-${k}`}>
                    <td className="px-5 py-1.5 w-1/2 font-mono text-slate-700">{k}</td>
                    <td className={'px-5 py-1.5 font-mono ' + (isEmpty(v) ? 'text-slate-400' : 'text-slate-900')}>
                      {fmtVal(v)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-5 py-2.5 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500">
          <div data-testid="jem-payload-footer">
            {rows.length} of {totalFields} fields shown
            {state ? ` · ${state.poll_count || 0} polls, ${state.change_count || 0} changes stored` : ''}
          </div>
          {state?.last_error && (
            <div className="text-rose-600">Last error: {state.last_error}</div>
          )}
        </div>
      </div>
    </div>
  );
}
